import { money, moneySigned, pct, pctSigned, price as fmtPrice } from '../lib/format';
import { PerpActions } from './AdjustPerp';
import { tickerOfContract } from '../state/contractExposure';
import type { PerpView } from '../state/derive';
import { fundingAndFees } from '../state/closePerp';

/* Leveraged book — §7.1. A summary strip, then one card per contract showing
   notional, margin, entry, unrealised in both dollars and percent, and a bar for
   the distance left to liquidation.

   These positions are entered by hand. The app reads marks and nothing else — it
   places no orders and holds no exchange keys (§1). */
/** What the book holds in shares of the same company, for a contract that has a
    shareholding beside it. Read on the card because a contract is one half of
    the bet on that name and the card is where you look at it. Keyed by ticker,
    uppercased. */
export interface AlsoHeld {
  weight: number;
  exposureWeight: number;
  /** How many contracts stand on this company in total. The exposure figure
      counts all of them, so a card cannot call it "with this one counted"
      unless this one is the only one. */
  contractCount: number;
}

export function LeveragedBook({
  perps, toDisplay, currency, say, onOpen, goneFromExchange, alsoHeld,
}: {
  perps: PerpView[];
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
  say?: (m: string) => void;
  /** Opens the contract's own screen, where every figure behind it lives. */
  onOpen?: (id: string) => void;
  /* API symbols the exchange has stopped reporting while the book still has
     them open. Passed in rather than read here, because this component draws a
     list and does not know there is such a thing as a broker connection. */
  goneFromExchange?: Set<string>;
  /* Passed in for the same reason `goneFromExchange` is: this draws a list of
     contracts and has no business knowing how a book computes a weight. */
  alsoHeld?: Map<string, AlsoHeld>;
}) {
  if (perps.length === 0) return <div className="empty">No contracts open.</div>;

  const margin = perps.reduce((s, p) => s + p.marginUsdt, 0);
  const unrealised = perps.reduce((s, p) => s + p.unrealisedUsd, 0);
  const onMargin = margin > 0 ? (unrealised / margin) * 100 : 0;

  return (
    <>
      <div className="card perp-strip">
        <div>
          <div className="k">Margin posted</div>
          <div className="v num">{money(toDisplay(margin), currency)}</div>
        </div>
        <div>
          <div className="k">Unrealised</div>
          <div className={`v num ${unrealised >= 0 ? 'up' : 'down'}`}>
            {moneySigned(toDisplay(unrealised), currency)}
          </div>
        </div>
        <div>
          <div className="k">On margin</div>
          <div className={`v num ${onMargin >= 0 ? 'up' : 'down'}`}>{pctSigned(onMargin)}</div>
        </div>
      </div>

      <div className="rows" style={{ marginTop: 8 }}>
        {perps.map((p) => {
          // The cursor sits where the mark is between liquidation and entry.
          // Clamped to the bar, so a contract deep in profit pins to the right.
          const cursorPct = Math.min(100, Math.max(0, p.distanceToLiq * 66));
          const close = p.distanceToLiqPct < 15;
          const held = alsoHeld?.get(tickerOfContract(p));
          return (
            <div key={p.id} className="card perp-card">
              <div className="perp-head">
                {onOpen ? (
                  <button className="btn btn-ghost btn-sm"
                          style={{ padding: 0, minHeight: 0, fontWeight: 600, fontSize: 14.5 }}
                          onClick={() => onOpen(p.id)}>
                    {p.display} ›
                  </button>
                ) : (
                  <span style={{ fontWeight: 600, fontSize: 14.5 }}>{p.display}</span>
                )}
                <span className="perp-side" data-side={p.side}>
                  {p.side.toUpperCase()} {p.leverage}x
                </span>
                {p.markStale ? <span className="tag" data-kind="muted">NO MARK</span> : null}
                {goneFromExchange?.has(p.symbol)
                  ? <span className="tag" data-kind="warn">CLOSED AT MEXC</span> : null}
                <span className="row-right">
                  <span className={`num ${p.unrealisedUsd >= 0 ? 'up' : 'down'}`}
                        style={{ fontWeight: 600, fontSize: 14.5 }}>
                    {moneySigned(toDisplay(p.unrealisedUsd), currency)}
                  </span>
                  <span className={`num ${p.returnOnMarginPct >= 0 ? 'up' : 'down'}`}
                        style={{ display: 'block', fontSize: 11.5, textAlign: 'right' }}>
                    {pctSigned(p.returnOnMarginPct)} on margin
                  </span>
                </span>
              </div>

              <div className="perp-grid">
                {/* The exchange card labels this "Size (USDT)" — contracts at the
                    current mark — so the two screens can be read side by side. */}
                <div><div className="k">Notional</div><div className="num">{money(toDisplay(p.notionalNowUsd), currency)}</div></div>
                <div><div className="k">Margin</div><div className="num">{money(toDisplay(p.marginUsdt), currency)}</div></div>
                <div><div className="k">Entry</div><div className="num">{fmtPrice(p.entryPrice)}</div></div>
                <div>
                  <div className="k">Mark</div>
                  <div className="num">{p.mark === null ? '—' : fmtPrice(p.mark)}</div>
                </div>
                <div><div className="k">Liquidation{p.liqEstimated ? ' · est.' : ''}</div><div className="num crit">{p.liqEstimated ? '≈ ' : ''}{fmtPrice(p.liqPrice)}</div></div>
                <div>
                  <div className="k">Take profit</div>
                  <div className="num">{p.takeProfit ? fmtPrice(p.takeProfit) : 'none set'}</div>
                </div>
              </div>

              <div
                className="liq-bar"
                role="meter"
                aria-valuenow={Number(p.distanceToLiqPct.toFixed(1))}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${p.display} is ${p.distanceToLiqPct.toFixed(1)} percent above its liquidation price`}
              >
                <div className="liq-cursor" style={{ left: `${cursorPct}%` }} />
              </div>
              {/* The exchange has stopped reporting it, and the book still has
                  it open.
                  *
                  * Nothing closes it automatically. Closing writes a realised
                  * result, and that result is the fill you got rather than
                  * something to work out from an absence — so the figure has to
                  * come from you. What was wrong before is that the app knew
                  * this every five minutes and said nothing here, while the
                  * free balance beside it reconciled to the penny. A book that
                  * is right about the cash and silent about the position reads
                  * as a book that is right about both. */}
              {goneFromExchange?.has(p.symbol) ? (
                <div className="note" data-tone="warn" style={{ marginTop: 8 }}>
                  MEXC is no longer reporting this contract, so it looks closed there while it is
                  still open here. Nothing was closed for you: what it came to is the fill you got,
                  and the account read does not carry it.
                  {onOpen ? ' Open it and use Close it with the result from the exchange.' : ''}
                </div>
              ) : null}

              <div style={{ fontSize: 11, marginTop: 6 }} className={close ? 'crit' : 'dim'}>
                {p.mark === null
                  ? 'No mark, so the distance to liquidation is unknown.'
                  : `${close ? '◆ ' : ''}${pct(p.distanceToLiqPct)} from the mark to liquidation`}
              </div>

              {/* The shares of the same company, so the card is not the only
                  place this bet is measured from. The book's ring counts the
                  shareholding alone; this says what the two come to together. */}
              {held ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.45 }}>
                  You hold the shares too. They are {pct(held.weight)} of the book,
                  {' '}{pct(held.exposureWeight)}
                  {held.contractCount === 1
                    ? ' with this counted.'
                    : ` with all ${held.contractCount} contracts on it counted.`}
                </div>
              ) : null}

              {p.autoMarginAddition ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.45 }}>
                  Auto margin addition is on, so a move against this pulls from your spare cash
                  before it liquidates. The liquidation price above is where it stands today, not
                  where it will stay.
                </div>
              ) : null}

              {/* Funding and fees: what MEXC counts as realised beyond the closes
                  recorded here. Read with the key and, until now, discarded — so a
                  position paying funding every eight hours showed none of it. */}
              {(() => {
                const gap = fundingAndFees(p);
                if (gap === null || Math.abs(gap) < 0.01) return null;
                return (
                  <div className="dim" style={{ fontSize: 11, marginTop: 5, lineHeight: 1.45 }}>
                    Funding and fees on this contract, by MEXC&rsquo;s count:{' '}
                    <span className={`num ${gap >= 0 ? 'up' : 'down'}`}>
                      {moneySigned(toDisplay(gap), currency)}
                    </span>
                    {gap < 0 ? ' paid' : ' received'}. MEXC puts realised at{' '}
                    <span className="num">{moneySigned(toDisplay(p.exchangeRealisedUsdt!), currency)}</span>,
                    the closes recorded here at{' '}
                    <span className="num">{moneySigned(toDisplay(p.realizedUsdt ?? 0), currency)}</span>.
                  </div>
                );
              })()}

              {p.realizedUsdt ? (
                <div className="dim" style={{ fontSize: 11, marginTop: 5 }}>
                  Realized so far on this contract:{' '}
                  <span className={`num ${p.realizedUsdt >= 0 ? 'up' : 'down'}`}>
                    {moneySigned(toDisplay(p.realizedUsdt), currency)}
                  </span>
                </div>
              ) : null}

              {/* Three things can happen to an open contract and each is a
                  different act: more went on, the book had it wrong, or some of
                  it came off. Only the last one books a result. */}
              {say && !p.closedAt ? (
                <div style={{ display: 'flex', marginTop: 10 }}>
                  <PerpActions perp={p} say={say} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}
